import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { ApiError } from "@/api/client";
import { useMe } from "@/api/hooks/admin";
import { useLogout } from "@/api/hooks/mutations";
import { NgonLogo } from "@/components/brand/NgonLogo";
import { initials } from "@/lib/format";

export function AdminLayout() {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  if (me.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-text-muted">
        Loading…
      </div>
    );
  }
  if (me.error) {
    if (me.error instanceof ApiError && me.error.status === 401) {
      return <Navigate to="/admin/login" replace />;
    }
    return (
      <div className="grid min-h-screen place-items-center px-6">
        <div className="w-full max-w-lg rounded-2xl border border-border bg-surface p-6">
          <h1 className="text-lg font-semibold">Admin unavailable</h1>
          <p className="mt-2 text-sm text-text-muted">
            Could not verify the current admin session. This does not look like a logout.
          </p>
          <p className="mt-3 text-sm text-danger">
            {me.error instanceof Error ? me.error.message : "Unexpected auth check failure."}
          </p>
        </div>
      </div>
    );
  }
  if (!me.data) return <Navigate to="/admin/login" replace />;

  async function onSignOut() {
    try {
      await logout.mutateAsync();
    } finally {
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-bg lg:grid lg:grid-cols-[240px_1fr]">
      <aside className="sticky top-0 hidden h-screen border-r border-border bg-surface px-4 py-6 lg:block">
        <div className="px-2 pb-6">
          <NgonLogo
            markClassName="size-8"
            textClassName="text-base"
            suffix={(
              <span className="ml-1 rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-medium text-text-muted">
                Admin
              </span>
            )}
          />
        </div>

        <SidebarSection label="Workspace">
          <SidebarLink to="/admin">Overview</SidebarLink>
          <SidebarLink to="/admin/role-rail">Role rail</SidebarLink>
          <SidebarLink to="/admin/candidates">Candidates</SidebarLink>
          <SidebarLink to="/admin/requests">Merchant requests</SidebarLink>
          <SidebarLink to="/admin/applications">Specialist applications</SidebarLink>
        </SidebarSection>

        <SidebarSection label="Account">
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-sm font-medium text-text-muted hover:bg-surface-alt hover:text-text"
          >
            Sign out
          </button>
        </SidebarSection>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-10 flex items-center gap-4 border-b border-border bg-surface px-4 py-4 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="inline-flex items-center rounded-md border border-border bg-surface px-3 py-2 text-sm font-medium text-text lg:hidden"
            aria-label="Open admin navigation"
          >
            Menu
          </button>
          <div className="ml-auto flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-primary to-accent text-[13px] font-semibold text-white">
              {initials(me.data.name, me.data.email)}
            </div>
          </div>
        </header>
        <section className="flex-1 px-4 py-6 pb-12 sm:px-6 lg:px-8 lg:py-7">
          <Outlet />
        </section>
      </div>

      {navOpen && (
        <div className="fixed inset-0 z-30 bg-slate-950/35 lg:hidden" onClick={() => setNavOpen(false)}>
          <aside
            className="h-full w-[280px] border-r border-border bg-surface px-4 py-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-2 pb-6 font-bold">
              <NgonLogo
                markClassName="size-8"
                textClassName="text-base"
                suffix={<span className="text-base">Admin</span>}
              />
              <button
                type="button"
                onClick={() => setNavOpen(false)}
                className="rounded-md border border-border px-2.5 py-1.5 text-sm text-text"
                aria-label="Close admin navigation"
              >
                Close
              </button>
            </div>

            <SidebarSection label="Workspace">
              <SidebarLink to="/admin">Overview</SidebarLink>
              <SidebarLink to="/admin/role-rail">Role rail</SidebarLink>
              <SidebarLink to="/admin/candidates">Candidates</SidebarLink>
              <SidebarLink to="/admin/requests">Merchant requests</SidebarLink>
              <SidebarLink to="/admin/applications">Specialist applications</SidebarLink>
            </SidebarSection>

            <SidebarSection label="Account">
              <button
                type="button"
                onClick={onSignOut}
                className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2.5 text-sm font-medium text-text-muted hover:bg-surface-alt hover:text-text"
              >
                Sign out
              </button>
            </SidebarSection>
          </aside>
        </div>
      )}
    </div>
  );
}

function SidebarSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="px-2.5 pb-2 text-[11px] font-semibold uppercase tracking-wider text-text-soft">
        {label}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function SidebarLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/admin"}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-2.5 py-2.5 text-sm font-medium ${
          isActive
            ? "bg-primary-soft text-primary-hover"
            : "text-text-muted hover:bg-surface-alt hover:text-text"
        }`
      }
    >
      {children}
    </NavLink>
  );
}
